import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminAuthModal from './AdminAuthModal';
import { AdminKeyService } from '../../services/adminKeyService';

type Status = 'idle' | 'checking' | 'success' | 'error';

const AdminLinkVerifierDialog: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const secret = React.useMemo(() => {
    const path = String(location?.pathname || '');
    if (!path.startsWith('/admin-')) return '';
    return path.slice('/admin-'.length).trim();
  }, [location?.pathname]);

  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState<Status>('idle');
  const [message, setMessage] = React.useState<string>('');
  const [authOpen, setAuthOpen] = React.useState(false);

  const runVerify = React.useCallback(async () => {
    setStatus('checking');
    setMessage('');

    try {
      if (!secret) throw new Error('Missing admin link key');

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 12000);

      const res = await fetch('/api/admin-fallback.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_fallback_verify_secret', candidate: secret }),
        signal: controller.signal,
      }).finally(() => window.clearTimeout(timeout));

      const raw = await res.text().catch(() => '');
      const json = (() => {
        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      })();

      if (!res.ok || !json?.ok) {
        const details = typeof json?.error === 'string' && json.error.trim() ? json.error.trim() : '';
        throw new Error(details || `Verification failed (${res.status})`);
      }

      if (!json?.valid) {
        throw new Error('Invalid admin link');
      }

      AdminKeyService.setLocalSecret(secret);
      setStatus('success');
      setMessage('Admin link verified. You can continue to the admin sign-in.');
      setAuthOpen(true);
    } catch (e: any) {
      const msg = e?.name === 'AbortError' ? 'Verification timed out. Please try again.' : (e?.message || 'Invalid admin link');
      setStatus('error');
      setMessage(msg);
    }
  }, [secret]);

  React.useEffect(() => {
    if (!secret) {
      setOpen(false);
      setStatus('idle');
      setMessage('');
      return;
    }

    setOpen(true);
    runVerify();
  }, [runVerify, secret]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenAuth = () => {
    setAuthOpen(true);
  };

  const title = status === 'success' ? 'Admin link verified' : status === 'error' ? 'Admin link failed' : 'Verifying admin link';

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Key: {secret ? `${secret.slice(0, 6)}…${secret.slice(-6)}` : '(missing)'}
            </Typography>

            {status === 'checking' ? (
              <Alert severity="info">
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <span>Checking your admin link…</span>
                </Stack>
              </Alert>
            ) : status === 'success' ? (
              <Alert severity="success">{message}</Alert>
            ) : status === 'error' ? (
              <Alert severity="error">{message}</Alert>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {status === 'checking' ? (
            <Button onClick={handleClose} variant="outlined">Close</Button>
          ) : status === 'success' ? (
            <>
              <Button onClick={handleClose} variant="outlined">Close</Button>
              <Button onClick={handleOpenAuth} variant="contained">Sign in / Sign up</Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose} variant="outlined">Close</Button>
              <Button onClick={runVerify} variant="contained">Retry</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <AdminAuthModal
        open={authOpen}
        onClose={() => {
          setAuthOpen(false);
          navigate('/', { replace: true });
        }}
        onSuccess={() => {
          setAuthOpen(false);
          navigate('/admin');
        }}
        prefillAdminSecret={secret}
      />
    </>
  );
};

export default AdminLinkVerifierDialog;
